import { ArrowPathIcon, FaceSmileIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Flag } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SymptomCheckerAssessmentMessageBubble, VideoModal } from '../components/chatbot/tabs/ConversationsTab';
import { ChatModeToggle } from '../components/common/ChatModeToggle';
import Modal from '../components/ui/Modal';
import { fetchImageUrl, getUserFullName } from '../helpers/helper';
import { getUser } from '../lib/storage';
import { agentInstanceService } from '../services/agentService';
import { chatbotService } from '../services/chatbotService';
import { sendMessageStreamApi } from '../services/chatService';
import { socketService } from '../services/socketService';
import { changeDocumentTitle, changeFavicon } from '../utility/document';
import { renderChatMarkdown } from '../utility/renderMarkdownText';
import { ShowImage } from '../utils/ShowImage';


export type TabId = 'general' | 'themes' | 'knowledge' | 'playground' | 'connect' | 'conversations';

export type IngestFlow = 'symptom-checker' | 'knowledge-base' | 'knowledge-base-qa';


export interface TabDef {
  id: TabId;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<{ chatbot: any }>;
}


export type ThemeState = {
  userMsgBg: string;
  userMsgFont: string;
  botMsgBg: string;
  botMsgFont: string;
  sendBtn: string;
  bg: string;
  inputTextBg: string;
  inputTextFont: string;
  inputBorder: string;
  fontFamily: string;
  welcome: string;
  placeholder: string;
  headerBg: any;
  headerTextFont: any
};

export enum Role {
  USER = "user",
  STAFF = "staff",
  ASSISTANT = "assistant"
}

export enum AnsweredBy {
  BOT = "bot",
  STAFF = "staff"
}


export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'staff';
  content: any;
  metadata?: any
  senderType?: 'user' | 'agent' | 'staff' | 'system-message' | string;
  type?: "normal" | "assessment";
};


export type ConversationSummary = {
  id: string;
  tenantId?: string | null;
  chatbotId?: string | null;
  status?: string | null;
  title?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  messageCount?: number;
  createdAt?: string;
  updatedAt?: string;
};


export type ConversationMessage = {
  id: string;
  conversationId: string;
  senderType: 'user' | 'agent' | string;
  message: string;
  messageType?: string;
  createdAt: string;
};


export type KBType = 'pdf' | 'text' | 'image' | 'url' | 'sitemap' | 'video';
export type KBStatus = 'processed' | 'processing' | 'error';

export let senderLabels: Record<string, string> = {
  bot: "Bot",
  user: "You"
}

export interface KBItem {
  id: string;
  name: string;
  type: KBType;
  size?: string;
  lastUpdated: string;
  status: KBStatus;
}


// IMAGE GALLERY TYPES AND COMPONENTS
interface ImageGroup {
  description: string;
  groupId: string;
  images: Array<{
    index: number;
    imageUrl: string;
    orderInGroup: number;
  }>;
}

type RightSectionItem = {
  id: number | string | null;
  title: string;
  descriptionLines: string[];
  image: string | null;
};

type RightSectionData = {
  sectionHeading: string
  sectionCountStats: {
    currentValue: number
    totalValue: number
  }
  data: Array<RightSectionItem>
  isShowSeparator?: boolean
}

interface TenantDbFaqMetadata {
  flow?: string;
  agentTypeCode?: string;
  capabilityCode?: string;
  sqlQuery?: string;
  rows?: any[];
  rowsReturned?: number;
  additionalInfo?: {
    accountNumber?: string;
    [key: string]: any;
  };
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const formatRightPanelValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "—";

    const looksLikeIsoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed);
    if (looksLikeIsoDate) {
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }

    return trimmed;
  }

  if (typeof value === "number") return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getDbRowTitle = (row: any, index: number): string => {
  return `Row ${index + 1}`;
};

const buildTenantDbRightSectionData = (
  userQuery: string,
  metadata: TenantDbFaqMetadata
): RightSectionData[] => {
  const sections: RightSectionData[] = [];

  if (metadata.sqlQuery) {
    sections.push({
      sectionHeading: `\n\nExecuted Query for: ${userQuery}`,
      sectionCountStats: {
        currentValue: 0,
        totalValue: 0,
      },
      data: [
        {
          id: "executed-query",
          title: "SQL Query",
          descriptionLines: metadata.sqlQuery
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean),
          image: null,
        },
      ],
    });
  }

  const rows = Array.isArray(metadata.rows) ? metadata.rows : [];
  const previewRows = rows.slice(0, 10);

  sections.push({
    isShowSeparator: true,
    sectionHeading: "Database Result",
    sectionCountStats: {
      currentValue: previewRows.length,
      totalValue:
        typeof metadata.rowsReturned === "number" &&
          metadata.rowsReturned > previewRows.length
          ? metadata.rowsReturned
          : 0,
    },
    data:
      previewRows.length > 0
        ? previewRows.map((row, index) => {
          if (isPlainObject(row)) {
            const descriptionLines = Object.entries(row).map(
              ([key, value]) => `${key}: ${formatRightPanelValue(value)}`
            );

            return {
              id: `db-row-${index}`,
              title: getDbRowTitle(row, index),
              descriptionLines,
              image: null,
            };
          }

          return {
            id: `db-row-${index}`,
            title: `Row ${index + 1}`,
            descriptionLines: [formatRightPanelValue(row)],
            image: null,
          };
        })
        : [
          {
            id: "no-rows",
            title: "No rows returned",
            descriptionLines: metadata.additionalInfo?.accountNumber
              ? [`Account: ${metadata.additionalInfo.accountNumber}`]
              : ["No matching records found"],
            image: null,
          },
        ],
  });

  return sections;
};

const buildKnowledgeBaseRightSectionData = (metadata: any): RightSectionData[] => {
  let relevantImages = metadata?.relevantImages;

  if (!relevantImages) return [];

  try {
    relevantImages =
      typeof relevantImages === "string"
        ? JSON.parse(relevantImages)
        : relevantImages;

    if (!Array.isArray(relevantImages) || relevantImages.length === 0) {
      return [];
    }

    return relevantImages.map((el: any) => ({
      sectionHeading: el.sectionHeading,
      sectionCountStats: {
        currentValue: el.images?.length ?? 0,
        totalValue: 0,
      },
      data: (el.images ?? []).map((image: any) => ({
        id: image.imageIndex,
        title: image.aboutImage,
        descriptionLines: [],
        image: image.imageUrl,
      })),
    }));
  } catch (imgErr) {
    console.warn("Failed to parse knowledge-base highlights", imgErr);
    return [];
  }
};

const ImageGallery: React.FC<{ images: ImageGroup[] }> = ({ images }) => {
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {images.map((group) => (
        <div key={group.groupId} className="space-y-3">
          {/* Group Heading */}
          <div className="px-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {group.description}
            </h3>
          </div>

          {/* Images in Group */}
          <div className="space-y-3">
            {group.images.map((img) => (
              <div
                key={`${group.groupId}-${img.index}`}
                className="w-full rounded-2xl overflow-hidden hover:shadow-md transition-shadow px-4 py-4 border-violet-400 bg-violet-50/60 shadow-[0_0_0_1px_rgba(139,92,246,0.35)]"
              >
                <img
                  src={img.imageUrl}
                  alt={group.description}
                  className="w-full h-auto object-cover max-h-64"
                  onError={(e) => {
                    e.currentTarget.src = '/default-image.png';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

function useQueryParam(name: string) {
  return new URLSearchParams(window.location.search).get(name);
}

export const AssessmentCarousel: React.FC<{
  content: any[];
}> = ({ content }) => {
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // 🔥 Prevent vertical scroll when swiping horizontally
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!content?.length) return null;

  return (
    <div className="mt-4">
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto overscroll-x-contain scroll-smooth snap-x snap-mandatory"
      >
        {content.map((item, index) => (
          <div
            key={index}
            className="w-[90%] flex-shrink-0 snap-start rounded-2xl border border-violet-200 bg-gradient-to-br from-white to-violet-50 p-5 shadow-sm"
          >
            {/* Text Content */}
            <div className="prose prose-sm max-w-none">
              {renderChatMarkdown(item.formattedText)}
            </div>

            {/* Divider */}
            <div className="my-4 h-px bg-violet-100" />

            {/* Video Section */}
            {item.metadata?.media?.length ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  Recommended Videos
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {item.metadata?.media.map((video: any, vidIndex: number) => (
                    <div
                      key={vidIndex}
                      onClick={() => setActiveVideo(video.url)}
                      className="group relative rounded-xl overflow-hidden bg-black aspect-video cursor-pointer"
                    >
                      <video
                        src={video.url}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        preload="metadata"
                      />

                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />

                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                          <svg
                            className="w-5 h-5 text-violet-600 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6 4l10 6-10 6V4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : ""}
          </div>
        ))}
      </div>
      {activeVideo && (
        <VideoModal
          videoSrc={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </div>
  );
};

const PublicChatWidget: React.FC = () => {
  const { chatbotId, clientId } = useParams<{ chatbotId: string; clientId: string }>();
  const [userId] = useState<string>(() => `guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const viewType: string | undefined = useQueryParam("view-type") || undefined
  const externalUserId: string | undefined = useQueryParam("external-user-id") || undefined
  const externalUserProfileId: string | undefined = useQueryParam("external-user-profile-id") || undefined
  const selectedConversationId: string | undefined = useQueryParam("conversation-id") || undefined
  // const role: 'staff' | 'user' | undefined = (useQueryParam("role") || undefined) as ('staff' | 'user' | undefined);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slug = useQueryParam("slug") || undefined;

  const user = getUser();
  const tenant = useMemo(() => user?.tenant, [user?.tenantId]);

  const fetchFaviconUrl = async () => {
    if (tenant?.s3FaviconObjectData) {

      const url = await fetchImageUrl(tenant?.s3FaviconObjectData)
      if (url) {
        changeFavicon(url);
      }
    }
  }
  useEffect(() => {
    if (tenant) {
      fetchFaviconUrl()
      changeDocumentTitle(tenant?.name || process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform');
    }
  }, [tenant]);

  // State
  const [chatbot, setChatbot] = useState<any>(null);
  const [theme, setTheme] = useState<any>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialMessages: ChatMessage[] = [];
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [imageGallery, setImageGallery] = useState<ImageGroup[]>([]);
  const [rightSectionHeading, setRightSectionHeading] = useState<string>("Key Highlights")
  const [rightSectionData, setRightSectionData] = useState<RightSectionData[]>([])
  const [activeRightSectionItemId, setActiveRightSectionItemId] = useState<number | string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [userTenantProfile, setUserTenantProfile] = useState<any>(null);

  const [answeredBy, setAnsweredBy] = useState<AnsweredBy>(AnsweredBy.BOT);
  const [isUserOnline, setIsUserOnline] = useState(false);
  const [userProfileConversation, setUserProfileConversation] = useState<any>(null);
  const convId = selectedConversationId || conversationId!

  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [showAuditMenu, setShowAuditMenu] = useState(false);
  const [showAuditQuestion, setShowAuditQuestion] = useState(false);
  const [auditUserMessage, setAuditUserMessage] = useState<any>(null);
  const [auditTargetMessage, setAuditTargetMessage] = useState<any>(null);
  const [audit, setAudit] = useState<any>({});
  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const openAuditMenu = async (message: any) => {
    console.log("Opening audit menu for message:", message);
    // find the last user message in the messages that occurs before the provided message
    const msgs = messages ?? [];
    let userMsg: any = null;
    const idx = msgs.findIndex((m: any) => m.id === message.id);

    if (idx >= 0) {
      for (let i = idx - 1; i >= 0; i--) {
        if (msgs[i]?.senderType === 'user') {
          userMsg = msgs[i];
          break;
        }
      }
    } else {
      // fallback: pick the latest user message with createdAt < message.createdAt
      const targetTime = message?.createdAt ? new Date(message.createdAt).getTime() : NaN;
      userMsg =
        [...msgs]
          .filter((m: any) => m?.senderType === 'user' && m?.createdAt && new Date(m.createdAt).getTime() < targetTime)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
    }

    if (!userMsg) {
      toast.error("No preceding user message found for this agent response.");
      return;
    }
    setAuditUserMessage(userMsg);
    console.log('Selected audit user message:', userMsg);
    setAuditTargetMessage(message);

    setAudit({
      conversationId: message.conversationId,
      userMessageId: userMsg.id,
      agentMessageId: message.id,
      correctedAnswer: message.message,
      chatbotId: chatbot?.id,
      reason: "wrong response",
    })
    // setAuditUserMessage()
    try {
      const res: any = await agentInstanceService.listAgentInstance?.({
        page: 1,
        limit: 50,
        isActive: true,
        chatBotId: chatbot?.id,
      })
      const raw = (res && (res.data?.agentInstances || res.agentInstances || res.data || res)) || [];
      console.log("Fetched agentInstances for audit:", raw);
      setAgentInstances(raw ?? []);
      // setShowAuditMenu(true);
    } catch (err) {
      console.error("Failed to load agentInstances", err);
    }
  };

  const type = userTenantProfile?.profileData?.profiles?.[0]?.type
  const role = type === "student" ? Role.USER : (type === "admin" || type === "staff") ? Role.STAFF : undefined;
  // useEffect(() => {
  //   if (messagesEndRef.current) {
  //     messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [messages]);

  // Add ref for the messages container
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Improved auto-scroll effect
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);  // Keep dependency on messages

  // Handle keyboard/viewport resize
  useEffect(() => {
    const handleResize = () => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isSending && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isSending]);

  // Load chatbot configuration and theme
  const loadChatbot = useCallback(async () => {
    if (!chatbotId) {
      setError('Chatbot ID is required');
      setIsLoading(false);
      return;
    }

    const loadChatbot = async () => {
      try {
        setIsLoading(true);
        const resp = await chatbotService.getChatbotById(encodeURIComponent(chatbotId));
        const chatbotData = (resp as any)?.data ?? resp;
        setChatbot(chatbotData);

        senderLabels.bot = chatbotData?.botDisplayName || 'Bot';
        senderLabels.user = chatbotData?.userDisplayName || 'You';

        // Extract theme settings
        const themeConfig = chatbotData?.themeConfig?.settings ||
          chatbotData?.themeSettings?.settings ||
          chatbotData?.settings || {};
        const themeSettings = chatbotData?.themeSettings?.settings ||
          chatbotData?.settings || {};
        setTheme({
          userMsgBg: themeConfig.userMessageBgColor || themeConfig.userMessageBg || 'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
          userMsgFont: themeConfig.userMessageFontColor || themeConfig.userMessageFont || '#ffffff',
          botMsgBg: themeConfig.botMessageBgColor || themeConfig.botMsgBg || '#ffffff',
          headerBgColor: themeConfig.headerBgColor || themeConfig.headerBgColor || '#ffffff',
          botMsgFont: themeConfig.botMessageFontColor || themeConfig.botMsgFont || '#000000',
          sendBtn: themeConfig.sendButtonColor || themeConfig.sendBtn || themeSettings.sendButtonColor || themeSettings.sendBtn || '#3b82f6',
          bg: themeConfig.bgColor || themeConfig.bg || '#f0f6ff',
          inputTextBg: themeConfig.inputTextBgColor || themeConfig.inputTextBg || '#ffffff',
          inputTextFont: themeConfig.inputTextFontColor || themeConfig.inputTextFont || '#000000',
          inputBorder: themeConfig.inputFieldBorderColor || themeConfig.inputBorder || '#e5e7eb',
          fontFamily: themeConfig.fontFamily || 'Inter, sans-serif',
          welcome: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?',
          placeholder: themeConfig.placeholderText || themeConfig.placeholder || 'Type your message...',
          headerTextFont: themeConfig.headerTextFont || "#fff"
        });

        // Add welcome message
        if (themeConfig.welcomeMessage || themeConfig.welcome) {
          setMessages([{
            id: 'welcome',
            role: 'assistant',
            content: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?'
          }]);
        } else {
          setMessages([{
            id: 'welcome',
            role: 'assistant',
            content: 'Hi! How can I help you today?'
          }]);
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('Failed to load chatbot:', err);
        setError('Failed to load chatbot configuration');
        setIsLoading(false);
      }
    };

    loadChatbot();
  }, [chatbotId]);

  useEffect(() => {
    if (!chatbotId) return;
    loadChatbot();
  }, [chatbotId]);

  useEffect(() => {
    if (chatbot && chatbotId && externalUserId && slug) {
      const agents = chatbot?.agents?.map((agent: any) => ({
        ...agent,
        parsedMetadata:
          typeof agent.agentInstance.agentType.metadata === "string"
            ? JSON.parse(agent.agentInstance.agentType.metadata)
            : agent.agentInstance.agentType.metadata,
      }));
      const useClientProfile: boolean = agents?.some((agent: any) => agent.parsedMetadata.useClientProfile === true) || false;
      if (useClientProfile) {
        const syncProfile = async () => {
          try {
            const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
            const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
            const tenantId = chatbotTenantId || lsTenantId;
            setIsLoading(true);
            const userProfile = await chatbotService.syncTenantUserProfile({
              chatbotId,
              tenantId,
              externalUserId,
              externalUserProfileId,
              slug
            });
            setUserTenantProfile(userProfile);
          } catch (err) {
            console.error('Failed to sync tenant user profile:', err);
          } finally {
            setIsLoading(false);
          }

        }

        syncProfile()
      }
    }
  }, [chatbotId, chatbot, externalUserId, externalUserProfileId, slug]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const resetChat = () => {
    const welcomeMsg = theme?.welcome || 'Hi! How can I help you today?';
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: welcomeMsg
    }]);
    setConversationId(undefined);
    setInput('');
    setImageGallery([]);
  };

  const loadConversation = async (conversationId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const conv: any = await chatbotService.singleConversationHistoryById(conversationId);
      // ensure messages are sorted by createdAt ascending
      const newMessages: any = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ta - tb;
        })
        .map((message: any) => ({
          ...message,
          metadata: message.metadata && typeof message.metadata === "string" ? JSON.parse(message.metadata) : message.metadata
        }));

      setConversationId(conv.id)
      setUserProfileConversation(conv.tenatUserProfile);
      setMessages(newMessages);
    } catch (err: any) {
      console.error('Failed to load conversation', err);
      setError('Failed to load conversation messages.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLatestConversation = async () => {
    try {
      if (!chatbotId) {
        return null
      }

      const conv: any = await chatbotService.listConversationHistory(chatbotId, {
        externalUserId,
        externalUserProfileId,
        limit: 1
      });

      if (!conv) return;

      const newMessages = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ta - tb;
        })
        .map((message: any) => ({
          ...message,
          metadata: message.metadata && typeof message.metadata === "string" ? JSON.parse(message.metadata) : message.metadata
        }));

      setConversationId(conv.id)
      setMessages(newMessages);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    reloadConversation()
  }, [selectedConversationId, externalUserId, externalUserProfileId])

  const reloadConversation = () => {
    if (selectedConversationId) {
      loadConversation(selectedConversationId!)
    } else if (externalUserId || externalUserProfileId) {
      loadLatestConversation();
    }
  }

  // const sendMessage = async () => {
  //   const trimmed = input.trim();
  //   if (!trimmed || isSending) return;

  //   const userMsg: ChatMessage = {
  //     id: `u-${Date.now()}`,
  //     role: 'user',
  //     content: trimmed,
  //   };
  //   setMessages((prev) => [...prev, userMsg]);
  //   setInput('');
  //   setIsSending(true);

  //   // Reset textarea height
  //   if (textareaRef.current) {
  //     textareaRef.current.style.height = 'auto';
  //   }

  //   try {
  //     const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
  //     const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
  //     const tenantId = chatbotTenantId || lsTenantId;

  //     if (!tenantId) {
  //       throw new Error('Tenant ID is required but not found');
  //     }

  //     const payload: any = {
  //       chatbotId: chatbotId,
  //       tenantId: tenantId,
  //       userId: userId,
  //       message: trimmed,
  //       viewType,
  //     };

  //     if (clientId) {
  //       payload.clientId = clientId;
  //     }

  //     if (conversationId) {
  //       payload.conversationId = conversationId;
  //     }

  //     console.log('Sending message:', { chatbotId, clientId, userId, tenantId, hasConversationId: !!conversationId });

  //     let response;
  //     try {
  //       response = await sendMessageApi(payload, false);
  //     } catch (err: any) {
  //       if (err?.status === 401 || err?.status === 403) {
  //         console.log('Retrying with authentication...');
  //         response = await sendMessageApi(payload, true);
  //       } else {
  //         throw err;
  //       }
  //     }

  //     console.log('🔍 API Response Debug:', response);

  //     const responseData = (response as any)?.data || response;

  //     const newConversationId = responseData?.conversationId || responseData?.conversation_id;
  //     if (!conversationId && newConversationId) {
  //       setConversationId(newConversationId);
  //     }

  //     const reply = responseData?.reply ||
  //       responseData?.message ||
  //       responseData?.response ||
  //       responseData?.text ||
  //       responseData?.content ||
  //       responseData?.answer ||
  //       (responseData?.data && (responseData.data.reply || responseData.data.message || responseData.data.response));

  //     const isErrorResponse = reply && (
  //       reply.toLowerCase().includes('unable to answer') ||
  //       reply.toLowerCase().includes('unable to') ||
  //       reply.toLowerCase().includes('i cannot') ||
  //       reply.toLowerCase().includes('i don\'t have') ||
  //       reply.toLowerCase().includes('no information')
  //     );

  //     if (isErrorResponse) {
  //       console.warn('⚠️ Backend returned error response:', reply);
  //       console.warn('⚠️ Full response data:', responseData);
  //     }

  //     const assistantMsg: ChatMessage = {
  //       id: `a-${Date.now() + 1}`,
  //       role: 'assistant',
  //       content: reply || 'Sorry, I am unable to answer this question with the available information.',
  //     };
  //     setMessages((prev) => [...prev, assistantMsg]);

  //     switch ((responseData?.metadata?.flow || null) as IngestFlow | null) {
  //       case "symptom-checker":
  //         setRightSectionHeading("Key Highlights of Assessment")
  //         setRightSectionData([])
  //         setActiveRightSectionItemId(null)

  //         if (responseData.metadata.matchedConditions?.length) {
  //           // Sort by relevanceScore in descending order (highest first)
  //           const matchingConditions = responseData.metadata.matchedConditions
  //             .sort((a: any, b: any) => b.relevanceScore - a.relevanceScore);

  //           const rightSectionData: RightSectionData[] = [{
  //             sectionHeading: "Matching Conditions",
  //             sectionCountStats: {
  //               currentValue: matchingConditions.length,
  //               totalValue: 0
  //             },
  //             data: matchingConditions.map((condition: any) => ({
  //               id: condition.docId,
  //               title: condition.conditionName,
  //               descriptionLines: [
  //                 `Probability: ${(condition.relevanceScore * 100).toFixed(0)}%`,
  //                 `Other symptoms: ${condition.symptomsText}`
  //               ],
  //               image: null
  //             }))
  //           }]
  //           setRightSectionData(rightSectionData)
  //         }

  //         break

  //       case "knowledge-base":
  //         setRightSectionHeading("Key Highlights")
  //         setActiveRightSectionItemId(null)

  //         // Extract relevant images from metadata
  //         let relevantImages = responseData?.metadata?.relevantImages || []
  //         if (relevantImages) {
  //           try {
  //             relevantImages = typeof relevantImages === 'string'
  //               ? JSON.parse(relevantImages)
  //               : relevantImages;

  //             if (Array.isArray(relevantImages) && relevantImages.length > 0) {
  //               const newRightSectionData: RightSectionData[] = relevantImages.map((el) => ({
  //                 sectionHeading: el.sectionHeading,
  //                 sectionCountStats: {
  //                   currentValue: el.images.length,
  //                   totalValue: 0
  //                 },
  //                 data: el.images.map((image: any) => ({
  //                   id: image.imageIndex,
  //                   title: image.aboutImage,
  //                   descriptionLines: [],
  //                   image: image.imageUrl
  //                 }))
  //               }))
  //               setRightSectionData([...rightSectionData, ...newRightSectionData])
  //             }
  //           } catch (imgErr) {
  //             console.warn('Failed to parse images:', imgErr);
  //           }
  //         }

  //         break

  //       default:
  //         setRightSectionHeading("Key Highlights")
  //         setRightSectionData([])
  //         setActiveRightSectionItemId(null)
  //     }

  //   } catch (err: any) {
  //     console.error('Failed to send message:', err);

  //     let errorMessage = 'Sorry, I encountered an error. Please try again.';
  //     if (err?.message) {
  //       errorMessage = err.message;
  //     } else if (err?.details?.error) {
  //       errorMessage = err.details.error;
  //     } else if (err?.details?.message) {
  //       errorMessage = err.details.message;
  //     } else if (typeof err === 'string') {
  //       errorMessage = err;
  //     }

  //     const errMsg: ChatMessage = {
  //       id: `e-${Date.now() + 2}`,
  //       role: 'assistant',
  //       content: errorMessage,
  //     };
  //     setMessages((prev) => [...prev, errMsg]);
  //   } finally {
  //     setIsSending(false);
  //   }
  // };



  const onNewMessageReceived = (data: any) => {
    if (data.senderType === 'system') {
      setMessages((prev) => [...prev, data]);
      return;
    }

    // if (role === Role.USER && data.answeredBy === AnsweredBy.BOT) {
    if (data.answeredBy === AnsweredBy.BOT) {
      return;
    }
    setMessages((prev) => [...prev, data]);
  };

  const handleSocketJoined = (joinRole?: string) => {
    const effectiveRole = joinRole || role;
    if (!convId || !effectiveRole) return;

    socketService.connect();
    socketService.joinRoom({
      conversationId: convId,
      role: effectiveRole,
      userInfo: userTenantProfile,
    });

    socketService.onMessage(onNewMessageReceived);

    socketService.onAnsweredByChanged((data: any) => {

      if (!data?.conversationId || data.conversationId !== convId) return;
      if (data.answeredBy === 'staff' || data.answeredBy === 'bot' || data.answeredBy === 'user') {
        setAnsweredBy(data.answeredBy);
      }
    });
    checkLiveStatus(convId);
  };


  useEffect(() => {
    if (role === Role.USER && conversationId) {
      handleSocketJoined();


      socketService.onAgentJoined(() => {

      });

      socketService.onAgentLeft(() => {

      });
    }
    return () => {
      socketService.disconnect();
    };
  }, [conversationId, role]);
  const checkLiveStatus = async (convId: string) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_CHAT_SOCKET_URL}/live/${encodeURIComponent(convId)}`);
      if (!resp.ok) {
        setIsUserOnline(false);
        return;
      }
      const data = await resp.json();
      setIsUserOnline(!!data?.live);

      if (data?.answeredBy === 'staff') {
        setAnsweredBy(AnsweredBy.STAFF);
      } else if (data?.answeredBy === 'bot') {
        setAnsweredBy(AnsweredBy.BOT);
      }
    } catch {
      setIsUserOnline(false);
    }
  };
  useEffect(() => {
    if (!(role === Role.STAFF)) return;
    if (!convId) return;


    handleSocketJoined('staff-passive');

    const handleUserJoined = (data: any) => {
      if (!data?.conversationId || data.conversationId !== convId) return;
      setIsUserOnline(true);
    };

    const handleUserLeft = (data: any) => {
      if (!data?.conversationId || data.conversationId !== convId) return;
      setIsUserOnline(false);
    };

    socketService.onUserJoined(handleUserJoined);
    socketService.onUserLeft(handleUserLeft);

    return () => {
      socketService.disconnect();
    };
  }, [role, selectedConversationId, conversationId, role, userTenantProfile]);


  useEffect(() => {
    if (!(role === Role.STAFF) || !selectedConversationId) return;

    if (answeredBy === AnsweredBy.STAFF) {
      socketService.setRole({
        conversationId: convId,
        role: 'staff',
      });


    } else {
      socketService.setRole({
        conversationId: convId,
        role: 'staff-passive',
      });
    }
  }, [answeredBy, role, selectedConversationId, role, userTenantProfile, conversationId]);

  const submitMultiAudit = async (toBeTrained: boolean) => {
    if (!auditTargetMessage || selectedAgents.length === 0) return;

    try {
      setLoading(true);
      for (const agentId of selectedAgents) {
        const body = {
          ...audit,
          agentInstanceId: agentId,
          toBeTrained: toBeTrained,
        };

        await agentInstanceService.createConversationAudit(body);
      }
      setLoading(false);
      reloadConversation();
      toast.success(`Audit submitted for ${selectedAgents.length} agents.`);


    } catch (err) {
      console.error(err);
      setLoading(false);
      toast.error("Failed to submit audit.");
    } finally {
      setLoading(false);
      setShowAuditMenu(false);
      setShowAuditQuestion(false);
      setAudit({});
      setSelectedAgents([]);
    }
  };

  const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
  const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
  const tenantId = chatbotTenantId || lsTenantId;
  if (!lsTenantId && tenantId) {
    localStorage.setItem("agentic_tenant_id", tenantId)
    localStorage.setItem("tenant_id", tenantId)
  }

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    // If agent is joined AND in staff mode, use socket instead of API
    if (answeredBy === AnsweredBy.STAFF && role) {
      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role,
        content: trimmed,
      };
      socketService.sendMessage({
        conversationId: convId,
        role,
        content: trimmed,
        userProfileId: userTenantProfile?.id,
        tenantUserProfile: userTenantProfile,
        senderType: role === Role.STAFF ? 'staff' : 'user',
      });
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      return;
    }

    const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
    const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
    const tenantId = chatbotTenantId || lsTenantId;
    if (!tenantId) {
      throw new Error('Tenant ID is required but not found');
    }

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    setMessages(prev => [...prev, userMsg]);
    // if(answeredBy === AnsweredBy.BOT){
    //   console.log(`here 3`)
    //   socketService.sendMessage({
    //     conversationId: convId,
    //     role: 'user',
    //     content: trimmed,
    //     userProfileId: userTenantProfile?.id,
    //     tenantUserProfile: userTenantProfile,
    //     senderType: 'user',
    //   });
    // }
    setInput('');
    setIsSending(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Add streaming message placeholder
    const streamingMsgId = `a-${Date.now()}`;
    const streamingMsg: ChatMessage = {
      id: streamingMsgId,
      role: 'assistant',
      content: <div className="flex gap-1">
        <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
          ●
        </span>
        <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
          ●
        </span>
        <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
          ●
        </span>
      </div>,
    };
    setMessages(prev => [...prev, streamingMsg]);

    const payload: any = {
      chatbotId,
      tenantId,
      userId,
      message: trimmed,
      viewType,
      additionalInputParams: {
        externalUserId,
        externalUserProfileId,
      },
      userTenantProfileId: userTenantProfile?.id
    };

    if (clientId) {
      payload.clientId = clientId;
    }

    if (conversationId) {
      payload.conversationId = conversationId;
    }

    try {
      let response;
      try {
        response = await sendMessageStreamApi(payload, false);
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          console.log('Retrying with authentication...');
          response = await sendMessageStreamApi(payload, true);
        } else {
          throw err;
        }
      }

      console.log('[Frontend] Response received:', {
        status: response.status,
        headers: {
          contentType: response.headers.get('content-type'),
          cacheControl: response.headers.get('cache-control'),
          connection: response.headers.get('connection'),
        },
        hasBody: !!response.body,
      });

      if (!response.ok) {
        console.error('[Frontend] Response not ok');
        throw new Error('Network error');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        console.error('[Frontend] No reader available');
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let buffer = '';
      let finalCompleteChunk: any = null;
      let hasReceivedFirstChunk = false;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[Frontend] Stream done');
          break;
        }

        if (value) {
          buffer += decoder.decode(value, { stream: true });
          console.log('[Frontend] Received chunk:', buffer.substring(0, 100));

          const parts = buffer.split('\n\n');
          buffer = parts[parts.length - 1];

          for (let i = 0; i < parts.length - 1; i++) {
            const line = parts[i].trim();
            if (!line.startsWith('data: ')) continue;

            try {
              const jsonStr = line.replace('data: ', '');
              const data: any = JSON.parse(jsonStr);

              console.log('[Frontend] Parsed data:', data.type);

              const isAssessment =
                data.metadata?.flow === "symptom-checker" &&
                data.metadata?.isAssessment === true;

              if (data.type === 'content') {
                // On VERY FIRST real content, clear the placeholder text
                if (!hasReceivedFirstChunk) {
                  hasReceivedFirstChunk = true;
                  accumulatedContent = '';
                }

                accumulatedContent += data.content;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === streamingMsgId
                      ? { ...msg, content: accumulatedContent, metadata: data.metadata, type: isAssessment ? "assessment" : "normal" }
                      : msg
                  )
                );
              } else if (data.type === 'complete') {
                finalCompleteChunk = data;

                const finalContent = data.content || accumulatedContent;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === streamingMsgId
                      ? { ...msg, content: data.content, metadata: data.metadata, type: isAssessment ? "assessment" : "normal" }
                      : msg
                  )
                );


                // When conversation is currently answered by bot, also emit the
                // final bot message over the socket so live staff viewers see it.
                // if (answeredBy === AnsweredBy.BOT) {
                //   const convIdForSocket =
                //     data.conversationId || selectedConversationId || conversationId;
                //   if (convIdForSocket) {
                //     socketService.sendMessage({
                //       conversationId: convIdForSocket,
                //       role: 'bot', // from socket server's perspective this is "non-user" side
                //       content: finalContent,
                //     });
                //   }
                // }

                if (!conversationId && data.conversationId) {
                  setConversationId(data.conversationId);
                }

                // if (data.metadata?.relevantImages) {
                //   setRightSectionData(data.metadata.relevantImages);
                // }

                switch ((data?.metadata?.flow || null) as IngestFlow | null) {
                  case "symptom-checker":
                    setRightSectionHeading("Key Highlights of Assessment")
                    setRightSectionData([])
                    setActiveRightSectionItemId(null)

                    if (data.metadata.matchedConditions?.length) {
                      // Sort by relevanceScore in descending order (highest first)
                      const matchingConditions = data.metadata.matchedConditions
                        .sort((a: any, b: any) => b.relevanceScore - a.relevanceScore);

                      const rightSectionData: RightSectionData[] = [{
                        sectionHeading: "Matching Conditions",
                        sectionCountStats: {
                          currentValue: matchingConditions.length,
                          totalValue: 0
                        },
                        data: matchingConditions.map((condition: any) => ({
                          id: condition.docId,
                          title: condition.conditionName,
                          descriptionLines: [
                            `Probability: ${(condition.relevanceScore * 100).toFixed(0)}%`,
                            `Other symptoms: ${condition.symptomsText}`
                          ],
                          image: null
                        }))
                      }]
                      setRightSectionData(rightSectionData)
                    }

                    break

                  case "knowledge-base":
                    setRightSectionHeading("Key Highlights")
                    setActiveRightSectionItemId(null)

                    const metadata = data?.metadata;
                    console.log(`metadata`, metadata)

                    if (metadata?.capabilityCode === "tenant-platform-db-faq") {
                      const existingRightSectionData = [...rightSectionData]
                      setRightSectionData([
                        ...buildTenantDbRightSectionData(trimmed, metadata),
                        ...existingRightSectionData
                      ]);
                      break;
                    }

                    setRightSectionData(buildKnowledgeBaseRightSectionData(metadata));
                    break

                  default:
                    setRightSectionHeading("Key Highlights")
                    setRightSectionData([])
                    setActiveRightSectionItemId(null)
                }
              }
            } catch (parseErr) {
              console.warn('[Frontend] Parse error:', parseErr);
            }
          }
        }

        // Safety: if stream ended without a `complete` but we got some chunks
        if (!finalCompleteChunk && accumulatedContent) {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === streamingMsgId
                ? { ...msg, content: accumulatedContent }
                : msg
            )
          );
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === streamingMsgId
            ? {
              ...msg,
              content: 'Sorry, I encountered an error. Please try again.',
            }
            : msg
        )
      );
    } finally {
      setIsSending(false);
    }
  };

  const chatShell = (
    <div
      className="flex flex-col h-full"
      style={{
        background:
          theme?.bg && theme.bg !== '#f0f6ff'
            ? theme.bg
            : 'linear-gradient(180deg, #f5f7ff 0%, #e3ebff 42%, #eef6ff 100%)',
        fontFamily: theme?.fontFamily || 'Inter, sans-serif',
      }}
    >
      {/* Chat Header */}
      <div
        className="h-14 flex items-center justify-between px-4 shadow-sm"
        style={{
          background:
            theme?.headerBgColor ||
            'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
          color: theme?.headerTextFont || '#ffffff',
        }}
      >
        <div className="flex items-center gap-2 font-semibold text-base">
          {chatbot?.s3ObjectData && (
            <div style={{ borderRadius: "50%", backgroundColor: "#ffffff", padding: "3px" }}>
              <ShowImage
                s3Params={chatbot?.s3ObjectData}
                src={process.env.REACT_APP_APPLICATION_LOGO_URL || "agentic-logo.png"}
                alt="bot"
                className="h-8 w-8 rounded-full object-cover"
                loading="eager"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.src = '/default-bot.png';
                }}
              />
            </div>
          )}

          <span style={{ color: chatbot?.headerTextFont }}>{chatbot?.name || 'Chat Support'}</span>
        </div>

        <div className="flex items-center gap-2">
          {selectedConversationId && role === Role.STAFF  && (
            <ChatModeToggle mode={answeredBy} onChange={(mode) => {
              socketService.setAnsweredBy({
                conversationId: convId,
                answeredBy: mode,
              });
            }} />
          )}

        </div>

        <button
          onClick={resetChat}
          className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Reset chat"
          title="Reset chat"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Messages Container */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m: any) => {
          if (m.senderType === 'system-message' || m.senderType === 'system') {
            const createdAtRaw = m.createdAt || m.metadata?.createdAt;
            const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
            const createdAtLabel = createdAtDate ? createdAtDate.toLocaleString() : '';

            return (
              <div key={m.id} className="flex justify-center my-2">
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <span className="h-px w-10 sm:w-24 bg-gray-200" />
                  <span className="whitespace-nowrap font-medium">
                    {m.content ?? m.message}
                  </span>
                  {createdAtLabel && (
                    <span className="ml-1 whitespace-nowrap text-gray-400">
                      • {createdAtLabel}
                    </span>
                  )}
                  <span className="h-px w-10 sm:w-24 bg-gray-200" />
                </div>
              </div>
            );
          }

          const sender = (m.senderType || m.role || '').toLowerCase();
          const isStaffViewer = role === 'staff';

          let isMine: boolean;
          if (isStaffViewer) {
            isMine =
              sender === 'staff' ||
              sender === 'agent' ||
              sender === 'bot' ||
              m.role === 'assistant';
          } else {
            isMine = sender === 'user' || m.role === 'user';
          }

          let content: any = ""
          try {
            content = JSON.parse(m.content ?? m.message)
          } catch (err) {
            content = m.content ?? m.message
          }

          const { isAssessment } = m.metadata || {};

          const isStringContent = ["string", "number", "boolean", null, undefined].includes(typeof m.content);
          const messageContent: string = m.content ?? m.message;

          const senderType = m.senderType || m.role;
          let senderLabel: string = '';

          let isAuditAllowed: boolean = false

          if (senderType === 'staff') {
            const profile = m.tenantUserProfile || m.userProfile;
            senderLabel = getUserFullName(profile?.profileData || profile) || 'Staff';

            isAuditAllowed = true
          } else if (senderType === 'bot' || senderType === 'agent' || m.role === 'assistant') {
            senderLabel = senderLabels.bot;

            isAuditAllowed = true
          } else if (senderType === 'user') {
            senderLabel = isMine ? senderLabels.user : getUserFullName(userProfileConversation?.profileData || userProfileConversation) || 'Student';
          } else {
            senderLabel = senderType || 'Unknown';
          }

          const createdAtRaw = m.createdAt || m.metadata?.createdAt;
          const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
          const createdAtLabel = createdAtDate ? createdAtDate.toLocaleString() : '';

          return (
            <div key={m.id} className={`flex items-center gap-3 ${isMine ? 'justify-end' : 'justify-start w-full'}`}>
              <div className={`${isMine ? '' : 'w-full max-w-full overflow-hidden'}`}>
                <div
                  className="w-min-full rounded-xl px-4 py-2.5 text-sm shadow-sm"
                  style={
                    isMine
                      ? {
                        background:
                          theme?.userMsgBg ||
                          'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
                        color: theme?.userMsgFont || '#ffffff',
                      }
                      : {
                        background: theme?.botMsgBg || '#ffffff',
                        color: theme?.botMsgFont || '#000000',
                        border: '1px solid #e5e7eb',
                      }
                  }
                >
                  {(senderLabel || createdAtLabel) && (
                    <div className="mb-1 flex items-center justify-between text-[11px] text-gray-500">
                      <span className="font-medium truncate max-w-[60%]">{senderLabel}</span>
                      {createdAtLabel && (
                        <span className="ml-2 whitespace-nowrap">{createdAtLabel}</span>
                      )}
                    </div>
                  )}
                  <div className="message-content space-y-6">
                    {isAssessment ? (
                      <SymptomCheckerAssessmentMessageBubble
                        metadata={m.metadata}
                      />
                    ) : !isStringContent ? messageContent : renderChatMarkdown(messageContent)}
                  </div>
                </div>
              </div>
              {(role === Role.STAFF && isAuditAllowed) && (
                <span
                  className="cursor-pointer text-agentic-text-secondary hover:text-agentic-accent-purple"
                  onClick={() => {
                    if (!m.hasAudits) {
                      setSelectedAgents([m.agentInstanceId]);
                      setShowAuditMenu(false);
                      setShowAuditQuestion(true);
                      openAuditMenu(m)
                    } else {
                      toast.info("This message has already been audited.");
                    }
                  }}   // ⭐ NEW FUNCTION
                >
                  {
                    m.hasAudits ? <Flag size={16} fill='#1e3a8a' color='#1e3a8a' /> : <Flag size={16} />
                  }

                </span>
              )}
            </div>
          );
        })}
        {/* {isSending && (
          <div className="flex justify-start">
            <div
              className="max-w-[80%] rounded-xl px-4 py-2.5 text-sm shadow-sm"
              style={{
                background: theme?.botMsgBg || '#ffffff',
                color: theme?.botMsgFont || '#000000',
                border: '1px solid #e5e7eb',
              }}
            >
              <div className="flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                  ●
                </span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                  ●
                </span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                  ●
                </span>
              </div>
            </div>
          </div>
        )} */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {
        ((!selectedConversationId) || (answeredBy === AnsweredBy.STAFF)) &&
        <>
          <div className="p-4 border-t" style={{ borderColor: theme?.inputBorder || '#e5e7eb' }}>
            <div
              className="flex items-center gap-2 rounded-xl px-3 py-2 shadow-sm"
              style={{
                background: theme?.inputTextBg || '#ffffff',
                border: `1px solid ${theme?.inputBorder || '#e5e7eb'}`,
              }}
            >
              <button
                className="p-1 transition-opacity hover:opacity-70 flex-shrink-0"
                style={{ color: theme?.botMsgFont || '#6b7280' }}
                aria-label="Emoji"
              >
                <FaceSmileIcon className="h-5 w-5" />
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInput}
                rows={1}
                placeholder={theme?.placeholder || 'Type your message...'}
                disabled={isSending}
                className="flex-1 resize-none bg-transparent overflow-auto outline-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-sm"
                style={{
                  maxHeight: '120px',
                  color: theme?.inputTextFont || '#000000',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button
                onClick={sendMessage}
                disabled={isSending || !input.trim()}
                className="p-2 rounded-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                style={{
                  background: theme?.sendBtn || '#3b82f6',
                  color: '#ffffff',
                }}
                aria-label="Send message"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Branding */}
          <div
            className="px-4 pb-2 text-center text-xs opacity-60"
            style={{ color: theme?.botMsgFont || '#6b7280' }}
          >
            Powered by {chatbot?.name || 'AI Chat'}
          </div>
        </>
      }

    </div>
  );

  if (isLoading) {
    return (
      <div className="h-screen w-full flex bg-[#f3f5fb]">
        <div className="w-full md:w-[36%] max-w-xl flex items-center justify-center border-r border-gray-200">
          <div className="text-sm text-gray-600">Loading chat...</div>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center bg-white">
          <div className="text-sm text-gray-400">Preparing resources…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex bg-[#f3f5fb]">
        <div className="w-full md:w-[36%] max-w-xl flex items-center justify-center border-r border-gray-200">
          <div className="text-sm text-red-600">{error}</div>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center bg-white">
          <div className="text-sm text-gray-400">Unable to load resources.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex bg-[#f3f5fb]">
      {/* Left column: chat panel */}
      <div className={`w-full ${viewType === "chat-only" ? "md:w-full" : "max-w-xl md:w-[36%]"} flex flex-col border-r border-gray-200`}>
        {chatShell}
      </div>

      {/* Right column: checklist panel */}
      {viewType === "chat-only" ? null : (
        <div className="hidden md:flex flex-1 flex-col m-4 ml-6 rounded-3xl bg-white shadow-[0_18px_45px_rgba(15,23,42,0.12)] overflow-hidden">
          {/* Step header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-400">
                {rightSectionHeading}
              </p>
            </div>
          </div>

          {/* Cards section */}
          <div className="h-[calc(100%-80px)] overflow-y-auto px-8 py-6 space-y-4 bg-gradient-to-b from-white via-white to-violet-50/40">
            {rightSectionData.map((section) => {
              return (
                <>
                  <div className="flex items-center gap-2">
                    {(section.sectionHeading || "").trim().length ? (
                      <p className="text-sm font-semibold text-gray-900">{section.sectionHeading}</p>
                    ) : ""}
                    {(section.sectionCountStats.currentValue || 0) !== 0 ? (
                      <span className="inline-flex h-6 items-center rounded-full bg-violet-50 px-2.5 text-xs font-medium text-violet-700">
                        {section.sectionCountStats.currentValue}
                        {(section.sectionCountStats.totalValue || 0) !== 0 ? ` / ${section.sectionCountStats.totalValue}` : ""}
                      </span>
                    ) : ""}
                  </div>
                  {section.data.map((item) => {
                    const isActive = activeRightSectionItemId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveRightSectionItemId(item.id)}
                        className={`w-full rounded-2xl border px-4 py-4 text-left transition-all duration-200 ${isActive
                          ? 'border-violet-400 bg-violet-50/60 shadow-[0_0_0_1px_rgba(139,92,246,0.35)]'
                          : 'border-gray-200 bg-white hover:border-violet-200 hover:shadow-sm'
                          }`}
                      >
                        <div className="flex items-stretch gap-4">
                          {item.image && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(item.image);
                              }}
                              className="group relative h-20 w-32 overflow-hidden rounded-xl bg-gray-100 shadow-inner transition-transform hover:scale-105 flex-shrink-0"
                            >
                              <img
                                src={item.image}
                                alt={item.title}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = '/default-image.png';
                                }}
                              />
                              {/* Hover overlay with zoom icon */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                <div className="opacity-0 transition-opacity group-hover:opacity-100">
                                  <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </button>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                            {(item.descriptionLines || []).map((description: string) => (
                              <p
                                key={description}
                                className="mt-1 text-xs text-gray-500 leading-relaxed"
                              >
                                {description}
                              </p>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {section.isShowSeparator && (
                    <div className="my-4 h-[3px] rounded-full bg-gradient-to-r from-transparent via-violet-300/80 to-transparent" />
                  )}
                </>
              )
            })}
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] rounded-lg bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-1 transition-transform hover:scale-110 hover:shadow-xl"
              aria-label="Close image viewer"
            >
              <svg
                className="h-6 w-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Image */}
            <img
              src={selectedImage}
              alt="Full view"
              className="max-h-[90vh] w-auto object-contain"
              onError={(e) => {
                e.currentTarget.src = '/default-image.png';
              }}
            />

            {/* Download Button */}
            {/* <a
              href={selectedImage}
              download
              className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700 shadow-lg"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </a> */}
          </div>
        </div>
      )}

      {showAuditMenu && (
        <Modal
          open={showAuditMenu}
          onClose={() => {
            setShowAuditMenu(false);
            setSelectedAgents([]);
          }}
          title="Flag Response & Assign Agents"
          size="md"
          loading={loading}
        >
          {/* <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"> */}
          <div>
            <h3 className="text-base font-semibold mb-3">Flag Response & Assign Agents</h3>

            <p className="text-xs text-gray-600 mb-3">
              Select one or more Agent Instances that should learn from this corrected response.
            </p>

            <div className="border rounded-lg max-h-60 overflow-y-auto p-2 space-y-2">
              {agentInstances.length === 0 && (
                <div className="text-xs text-gray-500">No agent instances found.</div>
              )}

              {agentInstances.map((ai) => (
                <label
                  key={ai.id}
                  className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedAgents.includes(ai.id)}
                    onChange={() => {
                      setSelectedAgents((prev) =>
                        prev.includes(ai.id)
                          ? prev.filter((x) => x !== ai.id)
                          : [...prev, ai.id]
                      );
                    }}
                  />
                  <div>
                    <div className="font-medium text-sm">{ai.name}</div>
                    <div className="text-xs text-gray-500">{ai.id}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Footer actions */}
            <div className="flex justify-between mt-5">
              <button
                className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                onClick={() => {
                  setSelectedAgents([]);
                  setShowAuditMenu(false);
                }}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                // onClick={submitMultiAudit}
                onClick={() => {
                  setShowAuditMenu(false);
                  setShowAuditQuestion(true);
                }}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${selectedAgents.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : ``
                  }`}
                style={{
                  background: theme?.sendBtn || '#3b82f6',
                  color: '#ffffff',
                }}
                disabled={selectedAgents.length === 0 || loading}
              >
                Submit Audit ({selectedAgents.length})
              </button>
            </div>
          </div>
          {/* </div> */}
        </Modal>

      )}

      {
        showAuditQuestion &&
        audit &&
        <Modal
          open={showAuditQuestion && Boolean(audit)}
          onClose={() => {
            setShowAuditQuestion(false);
            setAudit({});
          }}
          title="Flag Response & Provide Correct Answer"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                onClick={() => {
                  setShowAuditQuestion(false);
                  setAudit({});
                }}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                onClick={() => submitMultiAudit(false)}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${!audit.correctedAnswer
                  ? "bg-gray-400 cursor-not-allowed"
                  : ``
                  }`}
                style={{
                  background: theme?.sendBtn || '#3b82f6',
                  color: '#ffffff',
                }}
                disabled={!audit.correctedAnswer || loading}
              >
                Move to QA
              </button>
              <button
                onClick={() => submitMultiAudit(true)}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${!audit.correctedAnswer
                  ? "bg-gray-400 cursor-not-allowed"
                  : ``
                  }`}
                style={{
                  background: theme?.sendBtn || '#3b82f6',
                  color: '#ffffff',
                }}
                disabled={!audit.correctedAnswer || loading}
              >
                Move to QA & Train
              </button>
            </div>
          }
        >
          <div>
            {/* Question */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                User Question
              </label>
              <textarea
                className="w-full mt-1 border rounded-lg p-2 text-sm bg-gray-100"
                readOnly
                rows={2}
                value={auditUserMessage?.message || ''}
              />
            </div>

            {/* Answer (Editable) */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Bot Answer
              </label>
              <textarea
                className="w-full mt-1 border rounded-lg p-2 text-sm bg-gray-100"
                rows={4}
                value={auditTargetMessage?.message || ''}
                readOnly
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Correct Bot Answer (Edit below)
              </label>
              <textarea
                className="w-full mt-1 border rounded-lg p-2 text-sm"
                rows={4}
                value={audit?.correctedAnswer || ''}
                onChange={(e) => {
                  setAudit((prev: any) => ({ ...prev, correctedAnswer: e.target.value }));
                }}
              />
            </div>

            {/* <div className="flex justify-end">
              <button
                // disabled={savingId === audit.id}
                // onClick={() => moveToQA(audit)}
                onClick={submitMultiAudit}
                className={`px-5 py-2.5 rounded-lg text-white text-sm font-medium transition-all `}
                // ${savingId === audit.id
                //     ? "bg-green-400 cursor-not-allowed"
                //     : "bg-green-600 hover:bg-green-700"
                //   }`}
              >
                Move to QA & Train
              </button>
            </div> */}
          </div>
        </Modal>
      }
    </div>
  );
};

export default PublicChatWidget;
